import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import api from '../lib/axios';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Card, CardContent } from '../components/ui/Card';
import { ShieldCheck, ShieldAlert, ArrowLeft } from 'lucide-react';

const Security = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [setup, setSetup] = useState(null);   // { qr, secret } during enrolment
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    api.get('/auth/me')
      .then(res => setEnabled(!!res.data.two_factor_enabled))
      .catch(() => setError('Could not load your security settings.'))
      .finally(() => setLoading(false));
  }, []);

  const startSetup = async () => {
    setError(''); setNotice(''); setBusy(true);
    try {
      const res = await api.post('/auth/2fa/setup');
      setSetup(res.data);
      setCode('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start setup.');
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    setError(''); setBusy(true);
    try {
      await api.post('/auth/2fa/enable', { token: code });
      setEnabled(true);
      setSetup(null);
      setCode('');
      setNotice('Two-factor authentication is now ON. You will be asked for a code each time you log in.');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code.');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setError(''); setBusy(true);
    try {
      await api.post('/auth/2fa/disable', { token: code });
      setEnabled(false);
      setCode('');
      setNotice('Two-factor authentication has been turned off.');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      <header className="border-b" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <h1 className="text-2xl font-bold" style={{ color: theme.text }}>Security</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardContent className="pt-6 space-y-5">
            <div className="flex items-center gap-3">
              {enabled
                ? <ShieldCheck className="w-7 h-7 text-green-600 dark:text-green-400" />
                : <ShieldAlert className="w-7 h-7 text-yellow-600 dark:text-yellow-400" />}
              <div>
                <h2 className="text-lg font-semibold" style={{ color: theme.text }}>
                  Two-Factor Authentication
                </h2>
                <p className="text-sm" style={{ color: theme.textSecondary }}>
                  {loading ? 'Loading…' : enabled
                    ? 'Status: ON — a code from your authenticator app is required at login.'
                    : 'Status: OFF — your account is protected by password only.'}
                </p>
              </div>
            </div>

            <p className="text-sm" style={{ color: theme.textSecondary }}>
              Two-factor authentication adds a second step at login using a free authenticator
              app (Google Authenticator, Microsoft Authenticator, or Authy) on your phone.
            </p>

            {notice && <p className="text-sm text-green-600 dark:text-green-400">{notice}</p>}
            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

            {/* OFF and not mid-setup → offer to enable */}
            {!loading && !enabled && !setup && (
              <Button onClick={startSetup} disabled={busy}>
                {busy ? 'Please wait…' : 'Enable Two-Factor Authentication'}
              </Button>
            )}

            {/* Enrolment in progress → show QR + code field */}
            {!enabled && setup && (
              <div className="space-y-4">
                <ol className="text-sm list-decimal list-inside space-y-1" style={{ color: theme.text }}>
                  <li>Open your authenticator app and tap “add / scan QR code”.</li>
                  <li>Scan the image below (or type the setup key).</li>
                  <li>Enter the 6-digit code it shows, then click “Verify &amp; turn on”.</li>
                </ol>
                <div className="flex flex-col items-center gap-3">
                  <img src={setup.qr} alt="2FA QR code" className="w-44 h-44 rounded-lg bg-white p-2" />
                  <p className="text-xs break-all text-center" style={{ color: theme.textSecondary }}>
                    Setup key: <span className="font-mono">{setup.secret}</span>
                  </p>
                </div>
                <Input
                  label="6-digit code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
                <div className="flex gap-3">
                  <Button onClick={confirmEnable} disabled={busy || code.length < 6}>
                    {busy ? 'Verifying…' : 'Verify & turn on'}
                  </Button>
                  <Button variant="ghost" onClick={() => { setSetup(null); setCode(''); setError(''); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* ON → offer to disable (requires a current code) */}
            {!loading && enabled && (
              <div className="space-y-3">
                <p className="text-sm" style={{ color: theme.textSecondary }}>
                  To turn it off, enter a current code from your authenticator app.
                </p>
                <Input
                  label="6-digit code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
                <Button variant="danger" onClick={disable} disabled={busy || code.length < 6}>
                  {busy ? 'Please wait…' : 'Turn off two-factor authentication'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Security;
